import { Book, MoreVertical } from 'lucide-react'
import { Spin } from 'antd'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { VersionItem } from '@/types/api'
import type { CombinedColumnDef } from '@/api/dowload-excel.api'
import { formatDateTime } from '@/utils/format'

interface BuildColumnsOptions {
  pageIndex: number
  pageSize: number
  loadingId?: string | null
  canView?: boolean
  canUpdate?: boolean
  canDelete?: boolean
  onViewDetail?: (item: VersionItem) => void
  onUpdate?: (item: VersionItem) => void
  onDelete?: (item: VersionItem) => void
  onToggleStatus?: (item: VersionItem) => void
  onDownload?: (url: string, fileName?: string) => void
}

export function buildColumns({
  pageIndex,
  pageSize,
  loadingId,
  canView = true,
  canUpdate = true,
  canDelete = true,
  onViewDetail,
  onUpdate,
  onDelete,
  onToggleStatus,
  onDownload,
}: BuildColumnsOptions): CombinedColumnDef<VersionItem>[] {
  return [
    {
      id: 'stt',
      header: 'STT',
      size: 55,
      align: 'center',
      cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
    },
    {
      accessorKey: 'name',
      header: 'TÊN PHIÊN BẢN',
      align: 'left',
      cell: ({ row }) => (
        <button
          type="button"
          className="cursor-pointer text-[#CE7A58] hover:text-[#b8694a] font-medium"
          onClick={() => onViewDetail?.(row.original)}
        >
          {row.original.name}
        </button>
      ),
    },
    {
      id: 'fileApkInfo',
      accessorKey: 'fileApkInfo',
      header: 'FILE APK',
      align: 'center',
      cell: ({ row }) => {
        const fileInfo = row.original.fileApkInfo
        if (!fileInfo?.fileUrl) {
          return (
            <div className="text-center">{fileInfo?.originFileName || '-'}</div>
          )
        }
        return (
          <button
            type="button"
            className="text-center text-[#2196F3] cursor-pointer hover:underline"
            onClick={() =>
              onDownload?.(fileInfo.fileUrl!, fileInfo.originFileName)
            }
          >
            {fileInfo.originFileName || '-'}
          </button>
        )
      },
    },
    {
      id: 'fileGuildInfo',
      accessorKey: 'fileGuildInfo',
      header: () => (
        <div className="flex justify-center" title="Tài liệu hướng dẫn">
          <Book size={16} />
        </div>
      ),
      exportLabel: 'TÀI LIỆU HƯỚNG DẪN',
      align: 'center',
      cell: ({ row }) => {
        const fileInfo = row.original.fileGuildInfo
        if (!fileInfo?.fileUrl) {
          return (
            <div className="text-center">{fileInfo?.originFileName || '-'}</div>
          )
        }
        return (
          <button
            type="button"
            className="text-center text-[#2196F3] cursor-pointer hover:underline"
            onClick={() =>
              onDownload?.(fileInfo.fileUrl!, fileInfo.originFileName)
            }
          >
            {fileInfo.originFileName || '-'}
          </button>
        )
      },
    },
    {
      accessorKey: 'updatedAt',
      header: 'NGÀY CẬP NHẬT',
      size: 130,
      align: 'center',
      cell: ({ getValue }) => formatDateTime(getValue()),
    },
    {
      accessorKey: 'status',
      header: 'TRẠNG THÁI',
      size: 110,
      align: 'center',
      cell: ({ row }) => {
        const item = row.original
        const isLoading = loadingId === item.id

        return (
          <div className="flex justify-center">
            <Spin spinning={isLoading} size="small">
              <Switch
                checked={item.status === 1}
                onCheckedChange={() => {
                  if (!canUpdate) return
                  onToggleStatus?.(item)
                }}
                disabled={isLoading || !canUpdate}
              />
            </Spin>
          </div>
        )
      },
    },
    {
      id: 'actions',
      size: 55,
      exportable: false,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {canUpdate && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onUpdate?.(row.original)}
                >
                  Cập nhật
                </DropdownMenuItem>
              )}
              {canView && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onViewDetail?.(row.original)}
                >
                  Xem chi tiết
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  className="cursor-pointer text-red-600 focus:text-red-600"
                  onClick={() => onDelete?.(row.original)}
                >
                  Xóa
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ]
}
